import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Card, Btn, Empty, Modal } from "../../components/UI";
import { FLabel, FInput, FSelect, FTextarea } from "./FormControls";
import { SignaturePad } from "../../components/SignaturePad";
import { InfoTip } from "../../components/InfoTip";
import { Plus, ArrowLeft, Eye, Trash2, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const ENTRANCE_QUESTIONS = [
  "I am satisfied with the referral process locating treatment.",
  "It was easy to find the facility.",
  "The staff did a good job in reference to customer service.",
  "I received feedback from staff regarding services quickly.",
  "All of my questions were answered during intake.",
  "I feel safe in the environment.",
  "I am likely to recommend your organization to someone needing services.",
  "I have been treated with dignity and respect.",
  "I am satisfied with the program orientation.",
  "My expectations in reference to admissions were fully met.",
];
const LIKERT = ["Strongly Disagree", "Disagree", "Agree", "Strongly Agree", "Undecided"];
const SCORE = { "Strongly Disagree": 1, Disagree: 2, Agree: 3, "Strongly Agree": 4 };
const EMPTY = { race_ethnicity: "", age: "", gender: "", time_in_program: "", name: "", location: "", completed_with: "", answers: {}, comments: "", signature: "" };

const satisfaction = (answers) => {
  const vals = Object.values(answers || {}).map((v) => SCORE[v]).filter(Boolean);
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / (vals.length * 4)) * 100);
};

function LikertRow({ q, i, value, onChange }) {
  return (
    <div className="border-b border-[#F1EEE7] py-3 last:border-0" data-testid={`es-question-${i}`}>
      <p className="mb-2 text-sm font-medium text-[#3E2723]">{i + 1}. {q}</p>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {LIKERT.map((opt) => (
          <label key={opt} className="flex cursor-pointer items-center gap-1.5 text-sm text-[#5D4037]">
            <input type="radio" name={`q${i}`} data-testid={`es-q${i}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
              checked={value === opt} onChange={() => onChange(opt)} className="h-4 w-4 accent-[#4E342E]" />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

function SurveyForm({ onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  useEffect(() => {
    api.get("/users").then((r) => setStaff(r.data.filter((u) => u.status === "active"))).catch(() => {});
    api.get("/programs").then((r) => setLocations([...new Set(r.data.map((p) => p.location).filter(Boolean).concat(r.data.map((p) => p.name)))])).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/entrance-surveys", form);
      toast.success("Entrance survey saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Card className="mx-auto max-w-3xl p-7" data-testid="entrance-survey-form">
      <h2 className="mb-5 text-center font-heading text-lg font-bold text-[#3E2723]">Client Entrance Survey</h2>

      <h3 className="mb-3 font-heading text-sm font-bold text-[#3E2723]">A. Cultural Information</h3>
      <div className="grid gap-x-5 gap-y-4 md:grid-cols-2">
        <div><FLabel>Race/Ethnicity *</FLabel><FInput data-testid="es-race" value={form.race_ethnicity} onChange={f("race_ethnicity")} /></div>
        <div><FLabel>Age *</FLabel><FInput data-testid="es-age" type="number" min="0" max="120" value={form.age} onChange={f("age")} /></div>
        <div><FLabel>Gender *</FLabel><FInput data-testid="es-gender" value={form.gender} onChange={f("gender")} /></div>
        <div><FLabel>Time in Program *</FLabel><FInput data-testid="es-time-in-program" placeholder="e.g. 3 weeks" value={form.time_in_program} onChange={f("time_in_program")} /></div>
      </div>

      <h3 className="mb-3 mt-7 flex items-center gap-1.5 font-heading text-sm font-bold text-[#3E2723]">B. Optional Information
        <InfoTip text="Per CARF guidance the entrance survey may be completed anonymously. Name and location are optional so clients can respond freely." />
      </h3>
      <div className="grid gap-x-5 gap-y-4 md:grid-cols-2">
        <div><FLabel>Name</FLabel><FInput data-testid="es-name" value={form.name} onChange={f("name")} /></div>
        <div><FLabel>Location</FLabel>
          <FSelect data-testid="es-location" placeholder="Select location" value={form.location} onChange={f("location")}>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </FSelect></div>
        <div className="md:col-span-2 md:max-w-xs"><FLabel>Completed Survey with</FLabel>
          <FSelect data-testid="es-completed-with" value={form.completed_with} onChange={f("completed_with")}>
            {staff.map((u) => <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>)}
          </FSelect></div>
      </div>

      <h3 className="mb-1 mt-7 font-heading text-sm font-bold text-[#3E2723]">C. Entrance Survey Questions</h3>
      {ENTRANCE_QUESTIONS.map((q, i) => (
        <LikertRow key={i} q={q} i={i} value={form.answers[`q${i + 1}`]}
          onChange={(v) => setForm((s) => ({ ...s, answers: { ...s.answers, [`q${i + 1}`]: v } }))} />
      ))}

      <h3 className="mb-2 mt-7 font-heading text-sm font-bold text-[#3E2723]">D. Comments and Feedback</h3>
      <FLabel>1. Additional Comments</FLabel>
      <FTextarea data-testid="es-comments" rows={4} value={form.comments} onChange={f("comments")} />

      <h3 className="mb-2 mt-7 font-heading text-sm font-bold text-[#3E2723]">Electronic Signature *</h3>
      {user?.signature_image && !form.signature && (
        <button type="button" data-testid="es-use-saved-signature" onClick={() => setForm((s) => ({ ...s, signature: user.signature_image }))}
          className="mb-3 flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] px-3.5 py-2 text-xs font-semibold text-[#4E342E] hover:bg-[#F5F2EB]">
          <PenLine size={13} /> Use my saved signature
        </button>
      )}
      {form.signature ? (
        <div className="mb-2 rounded-xl border border-[#E5DFD3] bg-white p-3" data-testid="es-signature-preview">
          <img src={form.signature} alt="Signature" className="mx-auto h-20 object-contain" />
          <button type="button" data-testid="es-clear-signature" onClick={() => setForm((s) => ({ ...s, signature: "" }))}
            className="mx-auto mt-1 block text-xs font-semibold text-[#C62828] hover:underline">Clear signature</button>
        </div>
      ) : (
        <SignaturePad value={form.signature} onChange={(v) => setForm((s) => ({ ...s, signature: v }))} />
      )}

      <div className="mt-7 flex justify-center gap-3 border-t border-[#F1EEE7] pt-5">
        <Btn variant="ghost" data-testid="es-cancel-btn" onClick={onCancel}>Cancel</Btn>
        <Btn data-testid="es-save-btn" className="px-10" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</Btn>
      </div>
    </Card>
  );
}

export default function EntranceSurvey() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState("list");
  const [view, setView] = useState(null);
  useEffect(() => { document.title = "Client Entrance Survey | COCOA EHR"; }, []);
  const load = () => api.get("/entrance-surveys").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openView = (r) => api.get(`/entrance-surveys/${r.id}`).then((res) => setView(res.data)).catch((e) => toast.error(apiError(e)));
  const del = async (r) => {
    try { await api.delete(`/entrance-surveys/${r.id}`); toast.success("Survey deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (mode === "form") return (
    <div data-testid="entrance-survey-page">
      <button data-testid="es-back-btn" onClick={() => setMode("list")}
        className="mb-4 flex items-center gap-1.5 text-xs font-bold text-[#7B6157] hover:text-[#4E342E]"><ArrowLeft size={13} /> Back</button>
      <SurveyForm onSaved={() => { setMode("list"); load(); }} onCancel={() => setMode("list")} />
    </div>
  );

  return (
    <div data-testid="entrance-survey-page">
      <PageHeader title="Client Entrance Survey" subtitle="CARF-style satisfaction survey collected at program entry - may be completed anonymously"
        action={<Btn data-testid="es-new-btn" onClick={() => setMode("form")}><Plus size={15} /> New Entrance Survey</Btn>} />
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr>
              {["Date", "Name", "Age / Gender", "Time in Program", "Completed with", "Satisfaction", ""].map((h, i) => (
                <th key={i} className="border-y border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3 text-xs font-semibold text-[#4E342E] whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(rows || []).map((r) => {
                const sat = satisfaction(r.answers);
                return (
                  <tr key={r.id} data-testid={`es-row-${r.id}`} className="border-b border-[#F5F2EB] hover:bg-[#FDFCF8]">
                    <td className="px-4 py-2.5 tabular-nums text-[#6D4C41] whitespace-nowrap">{dayjs(r.created_at).format("MM/DD/YYYY")}</td>
                    <td className="px-4 py-2.5 font-semibold text-[#3E2723]">{r.name || <span className="font-normal italic text-[#84675B]">Anonymous</span>}</td>
                    <td className="px-4 py-2.5 text-[#5D4037]">{r.age} · {r.gender}</td>
                    <td className="px-4 py-2.5 text-[#5D4037]">{r.time_in_program}</td>
                    <td className="px-4 py-2.5 text-[#5D4037]">{r.completed_with || "-"}</td>
                    <td className="px-4 py-2.5">
                      {sat === null ? <span className="text-xs text-[#84675B]">-</span> : (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${sat >= 75 ? "bg-[#E8F5E9] text-[#2E7D32]" : sat >= 50 ? "bg-[#FFF8E1] text-[#876600]" : "bg-[#FFEBEE] text-[#C62828]"}`} data-testid={`es-sat-${r.id}`}>
                          <CheckCircle2 size={11} /> {sat}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1.5">
                        <button data-testid={`es-view-${r.id}`} aria-label="View survey" onClick={() => openView(r)}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F5F2EB] text-[#4E342E] hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                        {(user?.role === "admin" || user?.is_super_admin) && (
                          <button data-testid={`es-delete-${r.id}`} aria-label="Delete survey" onClick={() => del(r)}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F5F2EB] text-[#7B6157] hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows !== null && rows.length === 0 && <Empty text="No entrance surveys recorded yet - click New Entrance Survey to add the first one" />}
          {rows === null && <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div>}
        </div>
      </Card>

      <Modal open={!!view} onClose={() => setView(null)} title="Entrance Survey" wide>
        {view && (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1" data-testid="es-view-modal">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-3">
              {[["Date", dayjs(view.created_at).format("MM/DD/YYYY h:mm A")], ["Name", view.name || "Anonymous"], ["Race/Ethnicity", view.race_ethnicity],
                ["Age", view.age], ["Gender", view.gender], ["Time in Program", view.time_in_program],
                ["Location", view.location || "-"], ["Completed with", view.completed_with || "-"], ["Recorded by", view.created_by]].map(([l, v]) => (
                <div key={l}><div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{l}</div><div className="text-[#3E2723]">{v}</div></div>
              ))}
            </div>
            <div className="rounded-xl border border-[#F1EEE7]">
              {ENTRANCE_QUESTIONS.map((q, i) => (
                <div key={i} className="flex items-start justify-between gap-3 border-b border-[#F1EEE7] px-4 py-2 text-sm last:border-0">
                  <span className="text-[#5D4037]">{i + 1}. {q}</span>
                  <span className="shrink-0 font-semibold text-[#3E2723]">{view.answers?.[`q${i + 1}`] || "-"}</span>
                </div>
              ))}
            </div>
            {view.comments && <div className="rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm text-[#4E342E]"><span className="font-bold">Comments:</span> {view.comments}</div>}
            {view.signature && (
              <div className="rounded-xl border border-[#F1EEE7] p-3 text-center">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Electronic Signature</div>
                <img src={view.signature} alt="Signature" className="mx-auto h-16 object-contain" />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
